import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotificationDispatch } from '../context/NotificationContext';
import apiClient from '../api/client';

// Função para formatar o código do gift card
const formatGiftCardCode = (value: string) => {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const trimmed = cleaned.slice(0, 16);
    const parts = [];
    for (let i = 0; i < trimmed.length; i += 4) {
        parts.push(trimmed.slice(i, i + 4));
    }
    return parts.join('-');
};

// Função para formatar valor monetário com separador de milhar
const formatCurrency = (value: number) => {
    return value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export default function RedeemPage() {
    const { user, updateBalance } = useAuth();
    const { showNotification } = useNotificationDispatch();
    const [code, setCode] = useState('');
    const [status, setStatus] = useState({ loading: false, error: '', success: '' });

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formattedCode = formatGiftCardCode(e.target.value);
        setCode(formattedCode);
        // Limpar mensagens ao digitar
        if (status.error || status.success) {
            setStatus({ loading: false, error: '', success: '' });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (code.length < 19) return;

        setStatus({ loading: true, error: '', success: '' });

        try {
            const response = await apiClient.post('/giftcard/redeem', { code });
            const data = response.data;

            if (data.newBalance !== undefined) {
                updateBalance(data.newBalance);
            }

            setStatus({ loading: false, error: '', success: data.message });
            showNotification(data.message, 'success');
            setCode('');

        } catch (error: any) {
            const errorMessage = error.response?.data?.error || "Ocorreu um erro desconhecido.";
            setStatus({ loading: false, error: errorMessage, success: '' });
            showNotification(errorMessage, 'error');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 py-6 px-4">
            <div className="container mx-auto max-w-2xl space-y-4">

                {/* Card de Saldo (se logado) */}
                {user && (
                    <div className="bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 rounded-2xl shadow-xl p-6 text-white relative overflow-hidden">
                        {/* Padrão de fundo */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16"></div>
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12"></div>
                        </div>

                        <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                    <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-white/90 text-sm font-semibold mb-1">Seu Saldo Atual</p>
                                    <p className="text-3xl font-extrabold tracking-tight">R$ {formatCurrency(user.balance || 0)}</p>
                                </div>
                            </div>
                            <div className="hidden sm:block">
                                <div className="text-right">
                                    <p className="text-white/80 text-xs font-medium">Disponível para usar</p>
                                    <p className="text-white/90 text-sm font-semibold mt-1">em compras</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Card Principal */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
                    {/* Header com Ícone e Título */}
                    <div className="bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 px-6 py-8 text-white text-center relative overflow-hidden">
                        {/* Padrão de fundo */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute -top-20 -right-20 w-40 h-40 bg-white rounded-full"></div>
                            <div className="absolute -bottom-20 -left-20 w-32 h-32 bg-white rounded-full"></div>
                        </div>

                        <div className="relative">
                            {/* Ícone */}
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl shadow-xl mb-3">
                                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                </svg>
                            </div>

                            {/* Título */}
                            <h1 className="text-3xl font-extrabold mb-2">Resgatar Vale-Presente</h1>
                            <p className="text-blue-100">Insira o código e receba saldo instantâneo!</p>
                        </div>
                    </div>

                    {/* Formulário */}
                    <div className="p-6">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="gift-code" className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                                    <svg className="w-5 h-5 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                    </svg>
                                    Código do Vale-Presente
                                </label>
                                <div className="relative">
                                    <input
                                        id="gift-code"
                                        type="text"
                                        value={code}
                                        onChange={handleCodeChange}
                                        placeholder="XXXX-XXXX-XXXX-XXXX"
                                        className="w-full px-6 py-4 text-center text-2xl font-mono tracking-wider border-3 border-gray-200 rounded-xl focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/20 transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                                        required
                                        disabled={status.loading}
                                        maxLength={19}
                                    />
                                    {code.length > 0 && (
                                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                            {code.length === 19 ? (
                                                <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            ) : (
                                                <span className="text-sm text-gray-400 font-normal">{code.length}/19</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Digite o código no formato: XXXX-XXXX-XXXX-XXXX
                                </p>
                            </div>

                            {/* Mensagens de Status */}
                            {status.error && (
                                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 animate-shake">
                                    <div className="flex items-start gap-3">
                                        <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <div>
                                            <p className="font-bold text-red-900">Erro ao resgatar</p>
                                            <p className="text-red-700 text-sm mt-1">{status.error}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {status.success && (
                                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 animate-bounce-once">
                                    <div className="flex items-start gap-3">
                                        <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <div>
                                            <p className="font-bold text-green-900">Sucesso!</p>
                                            <p className="text-green-700 text-sm mt-1">{status.success}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Botão de Ação */}
                            <button
                                type="submit"
                                disabled={status.loading || code.length < 19}
                                className="w-full bg-gradient-to-r from-brand-blue to-blue-600 text-white font-bold py-4 px-6 rounded-xl text-lg hover:from-blue-600 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1 disabled:transform-none flex items-center justify-center gap-3"
                            >
                                {status.loading ? (
                                    <>
                                        <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Resgatando Vale...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Resgatar Saldo
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Informações Adicionais */}
                        <div className="mt-8 pt-6 border-t-2 border-gray-100 space-y-3">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <svg className="w-5 h-5 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Como funciona?
                            </h3>
                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <span className="text-brand-blue font-bold">1</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">Digite o código</p>
                                        <p className="text-xs text-gray-600">Insira o código de 16 caracteres do seu vale-presente</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <span className="text-blue-600 font-bold">2</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">Confirme o resgate</p>
                                        <p className="text-xs text-gray-600">Clique no botão para processar o vale-presente</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <span className="text-brand-blue font-bold">3</span>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900">Receba o saldo</p>
                                        <p className="text-xs text-gray-600">O valor é creditado instantaneamente na sua conta</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Aviso de Segurança */}
                        <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                            <div className="flex items-start gap-3">
                                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                <div>
                                    <p className="text-sm font-semibold text-blue-900">Seguro e Confiável</p>
                                    <p className="text-xs text-blue-700 mt-1">Cada código só pode ser usado uma vez e é validado em tempo real.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
